import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get } from '/api.js';
import { RoleMarks } from '/rolemark.js';
import { scoped, useCircleChoice } from '/circlechoice.js';
import { cachedGet, peek } from '/read.js';
import { RowSkeleton } from '/skeleton.js';
import { AddToCircle } from '/members.js';

const QUENCH = '#47C2A8';
const RUST = '#C25450';
const STEEL = '#3A4249';

function Back({ onDone }) {
  return html`<button class="secondary" data-testid="stats-done"
                      onClick=${onDone}>Back</button>`;
}

/** Offline is a designed-for state here: the shell is cached and /api is
 * deliberately not. A failed fetch must say so — swallowing it into the empty
 * state produces a confident, specific, wrong explanation, and leaving it
 * unhandled produces a screen that loads for ever with no way out. */
function Failed({ error, onDone }) {
  return html`
    <p class="error" data-testid="stats-error">
      Could not load that${error ? ` — ${error}` : ''}. If you are offline,
      this needs a connection: match data is never served from the cache,
      because a stale record that looks right is worse than none.
    </p>
    <${Back} onDone=${onDone} />`;
}

function Loading({ onDone }) {
  return html`<p class="sub">Loading…</p><${Back} onDone=${onDone} />`;
}

export function HeadToHead({ onDone, circleId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [circles] = useCircleChoice();
  const circleName = ((circles || []).find((c) => c.id === circleId)
                      || {}).name || '';
  useEffect(() => {
    // Scoped when opened from a circle. A record is earned in a circle, so a
    // page reached from Tuesday must not count Thursday's games.
    //
    // Cleared first: the label is derived from the prop and so changes on the
    // render the circle changes, while the rows only change when the fetch
    // lands. Without this the screen says "across all your circles" over one
    // circle's numbers for as long as the request takes — the same lie about
    // which circle a number belongs to that scoping them was meant to end.
    setRows(null);
    get(`/api/players/me/head-to-head${scoped(circleId)}`).then(setRows)
      .catch((e) => setError(e.detail || e.message));
  }, [circleId]);

  if (error) return html`<${Failed} error=${error} onDone=${onDone} />`;
  if (rows === null) return html`<${Loading} onDone=${onDone} />`;

  return html`
    <h1>Head to head</h1>
    ${/* Which circle these records are for. Reached from a circle it is that
          circle's; reached from the tab it is all of them, and saying so is
          the difference between a number and a number you can trust. */''}
    <label data-testid="h2h-circle">
      ${circleName || 'Across all your circles'}
    </label>
    ${rows.length === 0
      ? html`<p class="sub" data-testid="h2h-empty">
               No confirmed matches yet. A match against someone with an account
               only counts once they confirm it.
             </p>`
      : rows.map((r) => html`
          <div class="card" data-testid="h2h-row" key=${r.opponent_id}>
            <div style="display:flex;justify-content:space-between;align-items:baseline">
              <strong data-testid="h2h-name">
                <${RoleMarks} roles=${r.roles} only=${1} />${r.name}</strong>
              <span data-testid="h2h-record">${r.won}–${r.lost}</span>
            </div>
            <p class="sub" style="margin:.25rem 0 0">
              ${`${r.played} match${r.played === 1 ? '' : 'es'} · `}
              ${`${r.frames_for}–${r.frames_against} frames · `}
              ${r.streak > 0
                ? html`<span style="color:${QUENCH}">${r.streak} in a row</span>`
                : html`<span>${-r.streak} lost in a row</span>`}
            </p>
          </div>`)}
    <${Back} onDone=${onDone} />`;
}

export function Leaderboard({ onDone }) {
  // The SAME circle the dial is on, not this screen's own idea of a default.
  // It kept its own state and opened on the first circle you happened to be
  // in, so picking Thursday on the dial and tapping Leaders showed Tuesday —
  // two screens ranking two different circles with nothing saying so.
  const [circles, circleId, chooseCircle] = useCircleChoice();
  const [rows, setRowsRaw] = useState(null);
  const [forPath, setForPath] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  // Bumped to refetch the board after somebody joins it.
  const [round, setRound] = useState(0);

  useEffect(() => {
    get('/api/people').then(setAccounts)
      .catch((e) => setError(e.detail || e.message));
  }, [round]);

  useEffect(() => {
    // Tagged with the path it answers, which is null when there is no circle
    // to ask about — otherwise the board sits behind a placeholder waiting for
    // a request it is never going to make.
    if (circles !== null && circles.length === 0) {
      setRowsRaw([]); setForPath(null);
    }
  }, [circles]);

  // Known this render, not next. The rows were read from the cache inside an
  // effect, and effects run after the paint — so the board took an extra frame
  // to show numbers it already had, which is a flash of the previous screen.
  const path = circleId
    ? `/api/circles/${encodeURIComponent(circleId)}/leaderboard` : null;
  // Only THIS circle's rows count as known. `ignore` below already stopped a
  // late reply landing on the wrong circle; this stops the same happening to
  // what is on screen while the right reply is still on its way.
  const shown = forPath === path && rows !== null ? rows
    : (path ? peek(path) : undefined);

  useEffect(() => {
    if (!circleId || circles === null) return;
    // `ignore` drops a response that arrived after the circle changed again,
    // which would otherwise leave the previous board on screen under the new
    // circle's name.
    let ignore = false;
    // NOT cleared to null first. Blanking the board and refilling it threw the
    // layout away for the length of a round trip and rebuilt it, so everything
    // jumped twice for a list that usually comes back the same length. What is
    // on screen stays until the answer replaces it — and `ignore` is still
    // what stops a slow reply for the previous circle landing on this one.
    cachedGet(path)
      .then((r) => {
        if (!ignore) { setRowsRaw(r); setForPath(path); setError(null); }
      })
      .catch((e) => { if (!ignore) setError(e.detail || e.message); });
    return () => { ignore = true; };
  }, [circleId, round, circles]);

  // The error wins even when there is something cached to draw. Painting from
  // the last answer is for the gap BEFORE this one arrives; if it never
  // arrives, what is on screen is stale and looks current, which is the thing
  // sw.js refuses to do and this screen was explicitly built to avoid.
  if (error) return html`<${Failed} error=${error} onDone=${onDone} />`;
  if (shown === undefined || shown === null) {
    return html`
      <h1>Leaderboard</h1>
      <div class="stack" data-testid="board-waiting">
        <${RowSkeleton} height="4.2rem" /><${RowSkeleton} height="4.2rem" />
        <${RowSkeleton} height="4.2rem" />
      </div>
      <${Back} onDone=${onDone} />`;
  }

  const circle = (circles || []).find((c) => c.id === circleId);
  // The board IS the membership — every member is ranked, with no minimum-games
  // bar — so who is missing from it is who can be added.
  const ranked = new Set((shown || []).map((r) => r.player_id));
  const addable = accounts.filter((a) => !ranked.has(a.user_id));

  // "Nobody here yet" is true of an empty circle and false of somebody who has
  // no circle at all — and the second is what a new account always sees. One
  // message for both states is a confident, specific, wrong explanation of the
  // sort this file already refuses to give for a failed fetch.
  if ((circles || []).length === 0) {
    return html`
      <h1>Leaderboard</h1>
      <p class="sub" data-testid="board-none">
        You are not in a circle yet. A leaderboard ranks one circle, so record
        a match — that makes your first one.
      </p>
      <${Back} onDone=${onDone} />`;
  }

  return html`
    <h1>Leaderboard</h1>
    ${(circles || []).length > 1
      ? html`
        <select data-testid="board-circle" value=${circleId}
                onChange=${(e) => chooseCircle(e.target.value)}>
          ${(circles || []).map((c) => html`<option value=${c.id}>${c.name}</option>`)}
        </select>`
      // Named even when there is nothing to choose between. Hiding it left the
      // one-circle case — which is most people — reading a board with no idea
      // whose it was.
      : html`<label data-testid="board-circle-name">
               ${circle ? circle.name : ''}
             </label>`}

    ${shown.length === 0
      ? html`<p class="sub" data-testid="board-empty">
               Nobody here yet. A match counts once it is confirmed.
             </p>`
      : shown.map((r, i) => html`
          <div class="card" data-testid="board-row" key=${r.player_id}>
            <div style="display:flex;justify-content:space-between;align-items:baseline">
              <span><span class="sub">${i + 1}</span>${' '}
                <${RoleMarks} roles=${r.roles} only=${1} />${r.name}</span>
              <strong data-testid="board-elo">${Math.round(r.elo)}</strong>
            </div>
            <p class="sub" style="margin:.25rem 0 0">
              ${r.games} frame${r.games === 1 ? '' : 's'}
            </p>
          </div>`)}

    <div class="stack">
      <label>Add to this circle</label>
      <${AddToCircle} circleId=${circleId} addable=${addable}
                      onAdded=${() => setRound((n) => n + 1)} prefix="board" />
    </div>

    <${Back} onDone=${onDone} />`;
}

/** Rating after each match. One series, so no legend — the heading names it.
 *
 * Per MATCH, not per frame. A frame-by-frame line on an aggregate score is a
 * sawtooth: the synthesised frame order alternates winners, so the rating
 * zigzags a dozen points either side of itself and the chart shows the
 * synthesis rather than the player.
 *
 * The 1500 line is drawn because it is the only number on the axis that means
 * anything — above it you are winning more than expected, below it you are not.
 */
function Line({ points }) {
  const W = 320, H = 132, PAD = 8, BASE = 1500;
  const values = points.map((p) => p.after).concat(BASE);
  const lo = Math.min(...values), hi = Math.max(...values);
  const span = hi - lo < 8 ? 8 : hi - lo;          // never magnify noise
  const mid = (hi + lo) / 2;
  const top = hi - lo < 8 ? mid + 4 : hi;

  const x = (i) => PAD + (i * (W - 2 * PAD)) / Math.max(1, points.length - 1);
  const y = (v) => PAD + ((top - v) / span) * (H - 2 * PAD);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.after)}`).join(' ');
  const last = points[points.length - 1];
  const area = `${d} L${x(points.length - 1)},${y(BASE)} L${x(0)},${y(BASE)} Z`;

  return html`
    <svg viewBox="0 0 ${W} ${H}" width="100%" height=${H}
         role="img" data-testid="progress-chart"
         aria-label=${`Rating over ${points.length} match${points.length === 1 ? '' : 'es'}, now ${Math.round(last.after)}`}>
      <defs>
        <linearGradient id="cf-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color=${QUENCH} stop-opacity=".22"/>
          <stop offset="1" stop-color=${QUENCH} stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d=${area} fill="url(#cf-fill)"/>
      <line x1=${PAD} y1=${y(BASE)} x2=${W - PAD} y2=${y(BASE)}
            stroke=${STEEL} stroke-width="1" stroke-dasharray="3 4"/>
      <text x=${PAD} y=${y(BASE) - 5} fill="#6B7784"
            font-size="10" font-family="ui-monospace, monospace">1500</text>
      <path d=${d} fill="none" stroke=${QUENCH} stroke-width="2"
            stroke-linejoin="round" stroke-linecap="round"/>
      <circle cx=${x(points.length - 1)} cy=${y(last.after)} r="4"
              fill=${QUENCH} stroke="#191512" stroke-width="2"/>
    </svg>`;
}

export function Progression({ onDone, userId }) {
  const [points, setPoints] = useState(null);
  const [clear, setClear] = useState(null);
  const [error, setError] = useState(null);
  // The SAME circle the dial is showing. The line under a 1564 dial ending at
  // 1500 is the disagreement per-circle ratings exist to remove.
  const [circles, circleId] = useCircleChoice();
  const circleName = ((circles || []).find((c) => c.id === circleId)
                      || {}).name || '';

  useEffect(() => {
    if (circles === null) return;
    (async () => {
      try {
        // The id comes from the caller, which already has it — one round trip
        // rather than two on a screen opened at the table.
        const me = userId || (await get('/api/auth/state')).user_id;
        setPoints(await get(
          `/api/players/${me}/progression${scoped(circleId)}`));
        setClear(await get(
          `/api/players/${me}/clearances${scoped(circleId)}`));
      } catch (e) {
        setError(e.detail || e.message);
      }
    })();
  }, [userId, circleId, circles]);

  if (error) return html`<${Failed} error=${error} onDone=${onDone} />`;
  if (points === null) return html`<${Loading} onDone=${onDone} />`;

  const current = points.length ? points[points.length - 1].after : 1500;
  const change = points.reduce((sum, p) => sum + p.delta, 0);

  return html`
    <h1>Your rating</h1>
    ${/* Named, because it is one rating of several now. An unlabelled number
          is what made two circles look like the same circle. */''}
    ${circleName && html`
      <label data-testid="progress-circle">${circleName}</label>`}
    <p class="sub">Starts at 1500. Every match in this circle moves it.</p>

    <div class="card">
      <div class="figure" data-testid="progress-now">
        ${Math.round(current)}
      </div>
      <p class="sub" style="margin:.35rem 0 0" data-testid="progress-change">
        ${`${change >= 0 ? '+' : ''}${Math.round(change)} over ${points.length} `}
        ${points.length === 1 ? 'match' : 'matches'}
      </p>
    </div>

    ${clear && (clear.break_and_dish > 0 || clear.dish > 0) && html`
      <div class="card done" data-testid="clearances">
        <p class="sub" style="margin:0">
          ${`${clear.break_and_dish} break and dish · ${clear.dish} cleared from open play`}
        </p>
        <p class="sub" style="margin:.35rem 0 0;font-size:.75rem">
          Only counted for frames scored shot by shot.
        </p>
      </div>`}

    ${points.length > 1
      ? html`<${Line} points=${points} />`
      : html`<p class="sub" data-testid="progress-empty">
               Not enough frames to draw yet.
             </p>`}

    ${points.length > 0 && html`
      <label>Match by match</label>
      ${points.slice().reverse().slice(0, 12).map((p, i) => html`
        <div class="card ${p.delta >= 0 ? 'done' : 'warn'}"
             data-testid="progress-row" key=${i}
             style="display:flex;justify-content:space-between">
          <span class="sub">${Math.round(p.after)}</span>
          <span style="color:${p.delta >= 0 ? QUENCH : RUST}">
            ${p.delta >= 0 ? '+' : ''}${p.delta.toFixed(1)}
          </span>
        </div>`)}`}

    <${Back} onDone=${onDone} />`;
}
